/**
 * Types, constants, and port re-exports for the AuthSession domain.
 *
 * Kept in a dedicated file so that `reducer.ts`, `effects.ts`, `social.ts`,
 * and `index.ts` can all import from one place without creating circular deps.
 */

import type { AuthLogger } from '../../shared/auth-logger';
import type { AuthEventBus } from '../../shared/event-bus';
import type {
  AuthObservabilityService,
  AuthService,
  AuthSessionDeps,
  AuthSessionPort,
  AuthSessionSnapshot,
  AuthSessionStore,
  PendingAction,
  SessionPersistence,
} from '../../types/ports';
import type { User } from '../../types/user';

// ─── Re-exports for consumers ─────────────────────────────────────────────────

export type {
  AuthService,
  AuthObservabilityService,
  AuthSessionDeps,
  AuthSessionPort,
  AuthSessionSnapshot,
  AuthSessionStore,
  PendingAction,
  SessionPersistence,
};

export type { AuthEventBus };

// ─── Snapshot constants ───────────────────────────────────────────────────────

export const SERVER_SNAPSHOT: AuthSessionSnapshot = {
  user: null,
  isLoaded: false,
  isSignedIn: false,
};

// ─── Internal state ───────────────────────────────────────────────────────────

/**
 * The private mutable state owned by `AuthSession`.
 * Only the reducer may produce new values; effects read it as read-only input.
 */
export interface AuthSessionState {
  /** The currently authenticated user, or `null` when signed out. */
  user: User | null;
  /** `true` once the bootstrap sequence has completed (client-side only). */
  loaded: boolean;
}

/** Uninitialized state — used before `init()` is called. */
export const INITIAL_STATE: AuthSessionState = {
  user: null,
  loaded: false,
};

// ─── Actions ──────────────────────────────────────────────────────────────────

/**
 * Discriminated union of every action the reducer handles.
 *
 * - `AUTH_COMPLETE` — a sign-in completed successfully (email/password or social).
 * - `USER_UPDATED` — profile data changed in-place (e.g. after `setUser`).
 * - `SESSION_LOADED` — bootstrap finished; `user` is `null` if no session was found.
 * - `SESSION_CLEARED` — sign-out, account deletion, or an unrecoverable refresh error.
 */
export type AuthSessionAction =
  | { type: 'AUTH_COMPLETE'; user: User }
  | { type: 'USER_UPDATED'; user: User }
  | { type: 'SESSION_LOADED'; user: User | null }
  | { type: 'SESSION_CLEARED' };

// ─── Deps passed into AuthSession after init() ────────────────────────────────

export interface AuthSessionInternalDeps {
  service: AuthService;
  persistence: SessionPersistence;
  sessionStore: AuthSessionStore;
  logger: AuthLogger;
  observability: AuthObservabilityService;
  eventBus: AuthEventBus;
  replaceLocation: (url: string) => void;
}
